/* Persistence: autosave, live remote reload, layout menu, import/export,
   sign-in wiring. Storage goes through a StoreBackend (store.ts):
   device (localStorage) when signed out, Firestore when signed in. */
import { $, clearHistory, allIds, emptyDoc, pushUndo, state } from './state.ts';
import { fmtWhen, isEmptyDoc, shouldSnapshot } from './versions.ts';
import { exportPNG, fitView, renderExportCanvas, requestDraw } from './render.ts';
import { printHtml } from './print.ts';
import { loadNudge, recordSave, saveNudge } from './nudge.ts';
import { layoutName, parsePasted } from './paste.ts';
import { floorBelow, floorName, floorsOf, nextFloorLabel, projectOf } from './floors.ts';
import { exteriorWalls } from './rooms.ts';
import { clearPresence, publishPresence, watchPresence } from './cloud.ts';
import { colorFor, IDLE_MS, others, SEND_EVERY_MS, shouldSend } from './presence.ts';
import { renderPanel } from './panel.ts';
import { DeviceBackend, seedDeviceStore, type LayoutMeta, type StoreBackend } from './store.ts';
import { acceptInvite, CloudBackend, cloudReady, CollabBackend, createInvite, createShare, getShareId, grantPairCode, initCloud, listEditors, listGrants, loadSharedLayout, myEditGrant, onAuth, resolveShare, revokeEditor, revokeShare, signOut, unpairDevice, watchSharedLayout } from './cloud.ts';
import { showSignIn } from './authui.ts';
import { normalizeDoc } from './normalize.ts';

/** In-app replacements for window.alert / window.confirm — never use those. */
function dialog(msg: string, opts: { cancel?: boolean; ok?: string; danger?: boolean } = {}): Promise<boolean> {
  return new Promise(resolve => {
    const modal = $('#cModal'), okBtn = $('#cOk'), cancelBtn = $('#cCancel');
    $('#cMsg').textContent = msg;
    okBtn.textContent = opts.ok || 'OK';
    okBtn.className = opts.danger ? 'danger' : 'primary';
    cancelBtn.style.display = opts.cancel ? '' : 'none';
    modal.classList.add('show');
    const done = (v: boolean) => { modal.classList.remove('show'); cleanup(); resolve(v); };
    const ok = () => done(true), cancel = () => done(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') ok();
      if (e.key === 'Escape') cancel();
      e.stopPropagation();   // keep editor shortcuts quiet while the dialog is up
    };
    function cleanup() {
      okBtn.removeEventListener('click', ok);
      cancelBtn.removeEventListener('click', cancel);
      window.removeEventListener('keydown', onKey, true);
    }
    okBtn.addEventListener('click', ok);
    cancelBtn.addEventListener('click', cancel);
    window.addEventListener('keydown', onKey, true);
  });
}
export const notice = (msg: string): Promise<boolean> => dialog(msg);
export const confirmBox = (msg: string, ok = 'OK', danger = false): Promise<boolean> => dialog(msg, { cancel: true, ok, danger });

const saveBadge = $('#saveBadge');
export function setBadge(txt: string, cls?: string): void { saveBadge.textContent = txt; saveBadge.className = cls || ''; }

let backend: StoreBackend = new DeviceBackend();
let unwatch: (() => void) | null = null;
let lastList: LayoutMeta[] = [];
let pendingList: LayoutMeta[] | null = null;   // arrived while tab hidden

let saveTimer: ReturnType<typeof setTimeout> | null = null;
export function markDirty(): void {
  state.dirty = true;
  if (state.shareView) { setBadge('Sandbox — not saved', 'unsaved'); requestDraw(); return; }
  setBadge('Unsaved', 'unsaved');
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(saveNow, 700);
  requestDraw();
}
export function markDirtySoft(): void { // during drags: no autosave churn, save happens on pointerup markDirty
  state.dirty = true;
}

/** Runtime-only keys (screen-space hit targets etc.) start with '_' — keep them out of storage. */
const stripRuntime = (k: string, v: unknown) => k.startsWith('_') ? undefined : v;
const docJson = () => JSON.stringify(state.doc, stripRuntime);

/* ---- version history: snapshot the stored state before an editing burst ---- */
let lastSavedJson: string | null = null;          // what the store holds for state.name
const lastSnapAt = new Map<string, number>();     // per layout name, this session
async function maybeSnapshot(prevMtime: number): Promise<void> {
  const name = state.name!;
  if (!lastSavedJson || !shouldSnapshot(lastSnapAt.get(name) ?? null, Date.now())) return;
  try { if (isEmptyDoc(JSON.parse(lastSavedJson))) return; } catch { return; }
  lastSnapAt.set(name, Date.now());
  try { await backend.saveVersion(name, lastSavedJson, prevMtime); } catch { /* history is best-effort */ }
}

export async function saveNow(): Promise<void> {
  if (!state.name || state.shareView) return;   // share views never write anywhere
  if (saveTimer) clearTimeout(saveTimer);
  setBadge('Saving…', 'unsaved');
  // record the mtime BEFORE writing: the write's own watchList echo can land
  // before the save promise resolves, and must not read as a remote change
  // (that was the spurious "Layout changed on disk" banner)
  const m = Date.now() / 1000;
  const prev = state.lastMtime;
  state.lastMtime = m;
  const json = docJson();
  try {
    if (json !== lastSavedJson) await maybeSnapshot(prev);
    await backend.save(state.name, json, m);
    lastSavedJson = json;
    state.dirty = false;
    setBadge(backend.kind === 'cloud' ? 'Saved · cloud' : 'Saved · this device', '');
    if (backend.kind === 'device') countDeviceSave();
  } catch (err) {
    state.lastMtime = prev;
    if (state.collab && String((err as { code?: string })?.code || err).includes('permission-denied')) {
      setBadge('Access removed', 'offline');
      await exitCollab('Your access to this layout was removed by its owner. Your last change was not saved.');
      return;
    }
    setBadge('Offline', 'offline');
  }
}

/** Layout menu → Version history: list snapshots, restore one (undoable; the
    state being replaced gets kept as a version by the following autosave). */
export async function openHistory(): Promise<void> {
  const modal = $('#histModal'), list = $('#histList');
  list.innerHTML = '<p class="note">Loading…</p>';
  modal.classList.add('show');
  const vs = state.name && !state.shareView ? await backend.listVersions(state.name).catch(() => []) : [];
  list.innerHTML = '';
  if (!vs.length) {
    list.innerHTML = '<p class="note" id="histEmpty">No earlier versions yet. Playout keeps a snapshot of how a layout looked before each editing session (at most one every couple of minutes, up to 20), so you can always go back.</p>';
    return;
  }
  for (const v of vs) {
    const row = document.createElement('div'); row.className = 'histrow';
    const when = document.createElement('span'); when.textContent = fmtWhen(v.mtime);
    when.title = new Date(v.mtime * 1000).toLocaleString();
    const btn = document.createElement('button'); btn.textContent = 'Restore';
    btn.addEventListener('click', async () => { modal.classList.remove('show'); await restoreVersion(v.id); });
    row.append(when, btn);
    list.appendChild(row);
  }
}
async function restoreVersion(id: string): Promise<void> {
  const rec = await backend.loadVersion(state.name!, id).catch(() => null);
  if (!rec) { notice('That version is no longer available.'); return; }
  lastSnapAt.delete(state.name!);     // the state we're leaving gets snapshotted by the next save
  pushUndo();
  state.doc = normalizeDoc(Object.assign(emptyDoc(), rec.doc));
  state.sel.clear();
  markDirty(); renderPanel(); requestDraw();
}
$('#histClose').addEventListener('click', () => $('#histModal').classList.remove('show'));
$('#histModal').addEventListener('click', e => { if (e.target === $('#histModal')) $('#histModal').classList.remove('show'); });

export async function listLayouts(): Promise<LayoutMeta[]> {
  lastList = await backend.list();
  return lastList;
}

export async function loadLayout(name: string, opts: { preserveView?: boolean; preserveSel?: boolean } = {}): Promise<boolean> {
  const rec = await backend.load(name);
  if (!rec) return false;
  exitShareView();
  state.doc = normalizeDoc(Object.assign(emptyDoc(), rec.doc));
  state.name = name;
  state.lastMtime = rec.mtime;
  state.dirty = false;
  lastSavedJson = docJson();
  clearHistory();
  if (!opts.preserveView) fitView();
  if (!opts.preserveSel) state.sel.clear();
  else { const ids = allIds(); state.sel = new Set([...state.sel].filter(id => ids.has(id))); }
  setBadge(backend.kind === 'cloud' ? 'Saved · cloud' : 'Saved · this device', '');
  history.replaceState(null, '', '?layout=' + encodeURIComponent(name));
  $('#layoutBtn').textContent = name;
  $('#vrBtn').setAttribute('href', '/vr?name=' + encodeURIComponent(name));
  renderPanel(); requestDraw();
  renderFloorBar(); loadUnderlay();
  startPresence();
  return true;
}

export async function createLayout(name: string): Promise<void> {
  state.doc = emptyDoc(); state.name = name;
  lastSavedJson = null;
  state.underlay = null; renderFloorBar();
  state.sel.clear(); clearHistory();
  $('#layoutBtn').textContent = name;
  $('#vrBtn').setAttribute('href', '/vr?name=' + encodeURIComponent(name));
  history.replaceState(null, '', '?layout=' + encodeURIComponent(name));
  await saveNow();
  renderPanel(); requestDraw();
}

/* ---- remote changes (other tabs / devices / Claude via sync) ---- */
function onListUpdate(list: LayoutMeta[]): void {
  lastList = list;
  if (document.hidden) { pendingList = list; return; }
  applyRemote(list);
}
async function applyRemote(list: LayoutMeta[]): Promise<void> {
  if (!state.name) return;
  const it = list.find(l => l.name === state.name);
  if (it && it.mtime > state.lastMtime + 0.0005) {
    if (!state.dirty && !state.drag) {
      await loadLayout(state.name, { preserveView: true, preserveSel: true });
    } else {
      $('#reloadBar').classList.add('show');
    }
    state.lastMtime = it.mtime;
  }
}
$('#reloadYes').addEventListener('click', async () => {
  $('#reloadBar').classList.remove('show');
  await loadLayout(state.name!, { preserveView: true });
});
$('#reloadNo').addEventListener('click', () => {
  $('#reloadBar').classList.remove('show');
  saveNow();
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden) { if (state.dirty) saveNow(); }
  else if (pendingList) { const l = pendingList; pendingList = null; applyRemote(l); }
});

/* ---- backend switching + auth ---- */
async function setBackend(b: StoreBackend): Promise<void> {
  if (unwatch) { unwatch(); unwatch = null; }
  backend = b;
  unwatch = b.watchList(onListUpdate);
}

/** First sign-in: seed an empty cloud account with this device's layouts. */
async function migrateDeviceToCloud(cloud: CloudBackend): Promise<void> {
  const cl = await cloud.list();
  if (cl.length) return;
  const dev = new DeviceBackend();
  for (const m of await dev.list()) {
    const rec = await dev.load(m.name);
    if (rec) await cloud.save(m.name, JSON.stringify(rec.doc, stripRuntime));
  }
}

const authBtn = $('#authBtn');
const shareBtn = $('#shareBtn');
function renderAuthBtn(user: { displayName?: string | null; email?: string | null } | null): void {
  if (!cloudReady()) { authBtn.style.display = 'none'; shareBtn.style.display = 'none'; return; }
  authBtn.style.display = '';
  shareBtn.style.display = state.shareView ? 'none' : '';
  if (user) {
    authBtn.textContent = user.displayName || user.email || 'Account';
    authBtn.title = 'Account — pair a headset, sign out';
  } else {
    authBtn.textContent = 'Sign in';
    authBtn.title = 'Layouts currently save to this browser only. Sign in to sync them to an account.';
  }
}
let shareAfterSignIn = false;   // Share clicked while signed out → share once signed in
function openSignIn(opts: { note?: string; forShare?: boolean } = {}): void {
  shareAfterSignIn = !!opts.forShare;
  const note = $('#authNote');
  note.textContent = opts.note || '';
  note.style.display = opts.note ? 'block' : 'none';
  $('#authModal').classList.add('show');
  showSignIn($('#fbui'), () => $('#authModal').classList.remove('show'));
}
/* account menu: pairing + sign-out live here (signed out → straight to sign-in) */
const acctMenu = $('#acctMenu');
authBtn.addEventListener('click', e => {
  if (!cloudReady()) return;
  e.stopPropagation();
  const u = (window as any).__phUser;
  if (!u) { openSignIn(); return; }
  if (acctMenu.classList.contains('show')) { acctMenu.classList.remove('show'); return; }
  acctMenu.innerHTML = '';
  const who = document.createElement('div');
  who.className = 'who';
  who.textContent = 'Signed in as ' + (u.email || u.displayName || 'your account');
  acctMenu.appendChild(who);
  const sep = document.createElement('div'); sep.className = 'sep'; acctMenu.appendChild(sep);
  const entries: [string, () => void][] = [
    ['Headsets…', headsetsDialog],
    ['Sign out', async () => {
      if (await confirmBox('Sign out? Your layouts stay in your account; this browser switches to device-only layouts.', 'Sign out')) signOut();
    }],
  ];
  for (const [txt, fn] of entries) {
    const b = document.createElement('button');
    b.textContent = txt;
    b.addEventListener('click', () => { acctMenu.classList.remove('show'); fn(); });
    acctMenu.appendChild(b);
  }
  acctMenu.classList.add('show');
});
document.addEventListener('click', e => {
  if (!acctMenu.contains(e.target as Node) && (e.target as HTMLElement).id !== 'authBtn') acctMenu.classList.remove('show');
});
$('#authCancel').addEventListener('click', () => { shareAfterSignIn = false; $('#authModal').classList.remove('show'); });

/**
 * Boot the storage layer: init Firebase (if configured), pick the backend for
 * the current auth state, and keep switching as the user signs in/out.
 * Resolves once the first backend is ready.
 */
export function initStore(): Promise<void> {
  return new Promise(resolve => {
    let first = true;
    // Watchdog: if Firebase never reports an auth state (blocked SDK, captive
    // portal, broken IndexedDB), boot in device mode rather than hang on the
    // loading screen. A late auth event takes the normal mid-session path.
    setTimeout(async () => {
      if (!first) return;
      first = false;
      const dev = new DeviceBackend();
      await seedDeviceStore(dev);
      await setBackend(dev);
      renderAuthBtn(null);
      resolve();
    }, 8000);
    initCloud().finally(() => {
      onAuth(async rawUser => {
        // an anonymous session (headset pairing shares this origin's auth) is
        // NOT an account — the editor treats it as signed out
        const user = rawUser && !rawUser.isAnonymous ? rawUser : null;
        (window as any).__phUser = user;
        (window as any).__authSettled = false;   // tests wait for this: the account switch is done
        renderAuthBtn(user);
        if (user) {
          const cloud = new CloudBackend(user.uid);
          try { await migrateDeviceToCloud(cloud); } catch { /* fine: account already has layouts */ }
          await setBackend(cloud);
        } else {
          sharedWithMe = null;
          if (state.collab) { state.collab = null; $('#collabBar').classList.remove('show'); }
          const dev = new DeviceBackend();
          await seedDeviceStore(dev);
          await setBackend(dev);
        }
        if (first) {
          first = false;
          // landing page "Sign in" link arrives as /edit#signin
          if (!user && cloudReady() && location.hash === '#signin') openSignIn();
          if (user) sharedWithMe = await myEditGrant(user.uid);
          (window as any).__authSettled = true;
          resolve();
          return;
        }
        // auth changed mid-session: reopen the current layout from the new
        // backend if it exists there, else fall back to the most recent
        // Share-after-sign-in: if migration skipped (account already had
        // layouts), the layout being shared may not be in the account yet —
        // save the in-memory doc there under its name before reloading.
        if (user && shareAfterSignIn) await saveNow();
        const list = await listLayouts();
        if (state.name && list.some(l => l.name === state.name)) await loadLayout(state.name, { preserveView: true });
        else if (list.length) await loadLayout(list[0].name);
        else await createLayout('home');
        // Share was clicked while signed out: pick the flow back up now that
        // the layout lives in the account
        if (user && shareAfterSignIn) { shareAfterSignIn = false; await shareLayout(); }
        // only now, with the account switch complete: what's shared with me, and any invite being accepted
        if (user) {
          sharedWithMe = await myEditGrant(user.uid);
          if (pendingInvite) { const code = pendingInvite; pendingInvite = null; await acceptInviteCode(code); }
        }
        (window as any).__authSettled = true;
      });
    });
  });
}

/** Demo links (/edit?layout=<name>) for visitors who don't have the layout:
    import it from the bundled starter layouts into their store, then load. */
export async function importStarter(name: string): Promise<boolean> {
  try {
    const res = await fetch('/starter-layouts.json');
    if (!res.ok) return false;
    const starters: { name: string; doc: { walls?: unknown } }[] = await res.json();
    const s = starters.find(x => x.name === name);
    if (!s || !Array.isArray(s.doc.walls)) return false;
    await backend.save(name, JSON.stringify(s.doc, stripRuntime));
    return loadLayout(name);
  } catch { return false; }
}

/* ---- sharing ---- */
let shareUnwatch: (() => void) | null = null;

function exitShareView(): void {
  if (!state.shareView) return;
  state.shareView = null;
  if (shareUnwatch) { shareUnwatch(); shareUnwatch = null; }
  $('#shareBar').classList.remove('show');
  if (cloudReady()) shareBtn.style.display = '';
}

/** Open someone's share link (/edit?share=<id>): live view-only. */
export async function openShare(id: string): Promise<boolean> {
  if (!cloudReady()) return false;
  const ref = await resolveShare(id);
  if (!ref) return false;
  const rec = await loadSharedLayout(ref.uid, ref.name);
  if (!rec) return false;
  state.doc = normalizeDoc(Object.assign(emptyDoc(), rec.doc));
  state.name = ref.name;
  state.shareView = { id, ...ref };
  state.lastMtime = rec.mtime;
  state.dirty = false;
  clearHistory(); fitView(); state.sel.clear();
  setBadge('Shared · view only', '');
  $('#layoutBtn').textContent = ref.name + ' (shared)';
  $('#vrBtn').setAttribute('href', '/vr?share=' + encodeURIComponent(id));
  $('#shareBar').classList.add('show');
  shareBtn.style.display = 'none';
  shareUnwatch = watchSharedLayout(ref.uid, ref.name, r => {
    // live-follow the owner's edits unless the viewer is fiddling locally
    if (state.dirty || state.drag || r.mtime <= state.lastMtime + 1e-4) return;
    state.doc = Object.assign(emptyDoc(), r.doc);
    state.lastMtime = r.mtime;
    const ids = allIds();
    state.sel = new Set([...state.sel].filter(x => ids.has(x)));
    renderPanel(); requestDraw();
  });
  renderPanel(); requestDraw();
  return true;
}

async function saveShareCopy(): Promise<void> {
  const sv = state.shareView;
  if (!sv) return;
  const n = await ask('Save a copy as', sv.name);
  if (!n) return;
  exitShareView();
  state.name = n;
  state.dirty = true;
  await saveNow();
  history.replaceState(null, '', '?layout=' + encodeURIComponent(n));
  $('#layoutBtn').textContent = n;
  $('#vrBtn').setAttribute('href', '/vr?name=' + encodeURIComponent(n));
  await listLayouts();
  renderPanel(); requestDraw();
}
$('#shareCopyBtn').addEventListener('click', saveShareCopy);

/** Owner side: create/show the share links for the current layout. */
async function shareLayout(): Promise<void> {
  const user = (window as any).__phUser;
  if (!cloudReady()) {
    notice('Sharing needs the cloud, which isn’t set up in this build.');
    return;
  }
  if (!user) {
    openSignIn({
      forShare: true,
      note: 'Sharing needs a free account, so the link has a layout to point at. ' +
        'Your device layouts move into the account automatically — then the share links appear.',
    });
    return;
  }
  if (state.dirty) await saveNow();
  let id = await getShareId(user.uid, state.name!);
  if (!id) id = await createShare(user.uid, state.name!);
  const base = location.origin;
  ($('#shareLinkEdit') as HTMLInputElement).value = `${base}/edit?share=${id}`;
  ($('#shareLinkVR') as HTMLInputElement).value = `${base}/vr?share=${id}`;
  $('#inviteRow').style.display = 'none';
  $('#shareModal').classList.add('show');
  renderEditors();
}
/* ---- headset management (owner side): paired list + guided pairing ---- */
async function renderHsList(uid: string): Promise<void> {
  const list = $('#hsList');
  list.innerHTML = '<p class="note">Loading…</p>';
  const grants = await listGrants(uid).catch(() => []);
  list.innerHTML = '';
  if (!grants.length) {
    list.innerHTML = '<p class="note">No headsets paired yet.</p>';
    return;
  }
  for (const g of grants) {
    const row = document.createElement('div'); row.className = 'hsrow';
    const info = document.createElement('span');
    const nm = document.createElement('b'); nm.textContent = g.device || 'Headset';
    const when = document.createElement('span'); when.className = 'when';
    when.textContent = g.created
      ? 'paired ' + new Date(g.created).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : 'expires ' + new Date(g.exp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    info.append(nm, when);
    const btn = document.createElement('button'); btn.textContent = 'Unpair';
    btn.addEventListener('click', async () => { btn.disabled = true; await unpairDevice(g.deviceUid); renderHsList(uid); });
    row.append(info, btn);
    list.appendChild(row);
  }
}
function headsetsDialog(): void {
  const user = (window as any).__phUser;
  if (!cloudReady() || !user) {
    openSignIn({ note: 'Headsets pair to a free account — they get read-only access to the layouts in it.' });
    return;
  }
  const msg = $('#hsMsg'); msg.className = ''; msg.textContent = '';
  const inp = $('#hsCode') as HTMLInputElement; inp.value = '';
  $('#hsModal').classList.add('show');
  renderHsList(user.uid);
  inp.focus();
}
async function submitPairCode(): Promise<void> {
  const user = (window as any).__phUser;
  if (!user) return;
  const inp = $('#hsCode') as HTMLInputElement, msg = $('#hsMsg');
  const code = inp.value.trim();
  if (!code) { inp.focus(); return; }
  msg.className = ''; msg.textContent = '';
  const ok = await grantPairCode(user.uid, code, user.displayName || user.email || undefined);
  if (ok) {
    msg.className = 'ok';
    msg.textContent = 'Paired ✓ — the headset now shows your layouts. It can look, never touch.';
    inp.value = '';
    renderHsList(user.uid);
  } else {
    msg.className = 'bad';
    msg.textContent = 'That code didn’t work — codes expire after 10 minutes. Tap Pair on the headset for a fresh one.';
    inp.select();
  }
}
$('#hsGo').addEventListener('click', submitPairCode);
$('#hsCode').addEventListener('keydown', e => { if (e.key === 'Enter') submitPairCode(); e.stopPropagation(); });
$('#hsClose').addEventListener('click', () => $('#hsModal').classList.remove('show'));
const copyInput = (sel: string, btn: HTMLElement) => {
  const i = $(sel) as HTMLInputElement;
  i.select();
  navigator.clipboard.writeText(i.value).then(() => {
    const t = btn.textContent; btn.textContent = 'Copied ✓';
    setTimeout(() => { btn.textContent = t; }, 1400);
  });
};
shareBtn.addEventListener('click', shareLayout);
$('#copyEdit').addEventListener('click', () => copyInput('#shareLinkEdit', $('#copyEdit')));
$('#copyVR').addEventListener('click', () => copyInput('#shareLinkVR', $('#copyVR')));
$('#shareClose').addEventListener('click', () => $('#shareModal').classList.remove('show'));
$('#shareStop').addEventListener('click', async () => {
  const user = (window as any).__phUser;
  if (user && state.name) await revokeShare(user.uid, state.name);
  $('#shareModal').classList.remove('show');
});

/* ---- import / export ---- */
function exportCurrent(): void {
  if (!state.name) return;
  const a = document.createElement('a');
  a.download = state.name + '.json';
  a.href = URL.createObjectURL(new Blob([docJson()], { type: 'application/json' }));
  a.click();
  URL.revokeObjectURL(a.href);
}
function importFiles(): void {
  const inp = document.createElement('input');
  inp.type = 'file'; inp.accept = '.json,application/json'; inp.multiple = true;
  inp.addEventListener('change', async () => {
    let lastName: string | null = null;
    const rep = { placed: 0, dropped: 0 };
    let skipped = 0;
    for (const f of inp.files || []) {
      try {
        const doc = JSON.parse(await f.text());
        if (!doc || !Array.isArray(doc.walls)) { skipped++; continue; }
        const name = f.name.replace(/\.json$/i, '').trim() || 'imported';
        await backend.save(name, JSON.stringify(normalizeDoc(doc, rep), stripRuntime));
        lastName = name;
      } catch { skipped++; }
    }
    if (lastName) await loadLayout(lastName);
    const msgs: string[] = [];
    if (rep.placed) msgs.push(`${rep.placed} door/window${rep.placed > 1 ? 's' : ''} written as line segments were snapped onto the nearest wall.`);
    if (rep.dropped) msgs.push(`${rep.dropped} door/window${rep.dropped > 1 ? 's' : ''} couldn’t be placed on any wall and were left out.`);
    if (skipped) msgs.push(`${skipped} file${skipped > 1 ? 's' : ''} didn’t look like a Playout layout (needs a "walls" list).`);
    if (msgs.length) notice(msgs.join('\n'));
  });
  inp.click();
}

/** Rename the open layout (keeping its share link alive). */
export async function renameLayout(n: string): Promise<void> {
  await saveNow();
  const old = state.name!;
  const user = (window as any).__phUser;
  const shareId = backend.kind === 'cloud' && user ? await getShareId(user.uid, old) : null;
  state.name = n; await saveNow();
  await backend.remove(old);
  if (shareId && user) await createShare(user.uid, n, shareId);   // keep the share link alive
  $('#layoutBtn').textContent = n;
  $('#vrBtn').setAttribute('href', '/vr?name=' + encodeURIComponent(n));
  history.replaceState(null, '', '?layout=' + encodeURIComponent(n));
  await listLayouts(); renderFloorBar();
}

/* ---- collaboration: invited editors work inside the owner's account ---- */
let pendingInvite: string | null = null;
let sharedWithMe: { owner: string; layouts: string[] } | null = null;
const editorLabel = () => { const u = (window as any).__phUser; return u ? (u.displayName || u.email || 'someone') : 'someone'; };

/** /edit?invite=<code>: accept once signed in (asking to sign in first if needed). */
export async function openInvite(code: string): Promise<boolean> {
  if (!cloudReady()) return false;
  const user = (window as any).__phUser;
  if (!user) {
    pendingInvite = code;
    openSignIn({ note: 'You’ve been invited to edit a layout. Sign in (free) to accept — your edits will carry your name.' });
    return true;
  }
  return acceptInviteCode(code);
}
async function acceptInviteCode(code: string): Promise<boolean> {
  const user = (window as any).__phUser;
  const inv = await acceptInvite(code, { uid: user.uid, editor: editorLabel() }).catch(() => null);
  if (!inv) { notice('This invitation is no longer valid — ask the owner for a new link.'); return false; }
  sharedWithMe = { owner: inv.uid, layouts: inv.layouts };
  await enterCollab(inv.uid, inv.layouts, inv.layouts[0]);
  return true;
}
export async function enterCollab(owner: string, layouts: string[], open: string): Promise<void> {
  if (state.dirty && !state.collab) await saveNow();
  exitShareView();
  state.collab = { owner, layouts };
  await setBackend(new CollabBackend(owner, layouts));
  $('#collabBar').classList.add('show');
  shareBtn.style.display = 'none';
  const ok = await loadLayout(open);
  if (!ok) await exitCollab('That layout is no longer shared with you.');
}
export async function exitCollab(why?: string): Promise<void> {
  const user = (window as any).__phUser;
  stopPresence();
  state.collab = null; state.dirty = false;
  $('#collabBar').classList.remove('show');
  if (cloudReady() && user) shareBtn.style.display = '';
  await setBackend(user ? new CloudBackend(user.uid) : new DeviceBackend());
  const list = await listLayouts();
  if (list.length) await loadLayout(list[0].name); else await createLayout('home');
  if (why) notice(why);
}
$('#collabBack').addEventListener('click', () => exitCollab());

/** Share modal, "Edit together": mint an invite link; list / revoke editors. */
async function renderEditors(): Promise<void> {
  const user = (window as any).__phUser; if (!user) return;
  const list = $('#editorList'); list.innerHTML = '';
  const eds = await listEditors(user.uid).catch(() => []);
  const mine = eds.filter(e => e.layouts.includes(state.name!));
  if (!mine.length) { list.innerHTML = '<p class="note">Nobody else can edit this layout yet.</p>'; return; }
  for (const e of mine) {
    const row = document.createElement('div'); row.className = 'histrow';
    const who = document.createElement('span'); who.textContent = e.editor;
    const btn = document.createElement('button'); btn.textContent = 'Remove';
    btn.addEventListener('click', async () => { btn.disabled = true; await revokeEditor(e.deviceUid); renderEditors(); });
    row.append(who, btn); list.appendChild(row);
  }
}
$('#inviteBtn').addEventListener('click', async () => {
  const user = (window as any).__phUser; if (!user || !state.name) return;
  const code = await createInvite(user.uid, [state.name]);
  ($('#inviteLink') as HTMLInputElement).value = `${location.origin}/edit?invite=${code}`;
  $('#inviteRow').style.display = '';
});
$('#copyInvite').addEventListener('click', () => { const i = $('#inviteLink') as HTMLInputElement; i.select(); navigator.clipboard?.writeText(i.value).catch(() => {}); });

/* ---- presence: see where collaborators are (cloud mode; nobody publishes while alone) ---- */
let presenceOff: (() => void) | null = null;
let presenceKey = '';
let lastSent: { x: number; y: number; at: number } | null = null;
let lastMoveAt = 0;
const presenceOwner = () => state.collab ? state.collab.owner : ((window as any).__phUser?.uid as string | undefined);
function startPresence(): void {
  const owner = presenceOwner(), me = (window as any).__phUser?.uid as string | undefined;
  const key = owner && state.name ? owner + '/' + state.name : '';
  if (key && key === presenceKey) return;      // same layout reloaded (a remote change): keep the watch, keep our entry
  stopPresence();
  if (!cloudReady() || !owner || !me || !state.name || state.shareView || backend.kind !== 'cloud') return;
  presenceKey = key;
  presenceOff = watchPresence(owner, state.name, all => { state.presence = all; requestDraw(); });
  if (state.collab) publishPresence(owner, state.name, me, { x: state.lastMouse.x, y: state.lastMouse.y, name: editorLabel(), color: colorFor(me) }).catch(() => {});   // "I'm here"
}
function stopPresence(): void {
  if (presenceOff) { presenceOff(); presenceOff = null; }
  const [owner, name] = presenceKey.split('/'); const me = (window as any).__phUser?.uid;
  if (owner && name && me) clearPresence(owner, name, me);
  presenceKey = ''; lastSent = null;
  if (state.presence.size) { state.presence = new Map(); requestDraw(); }
}
/** Called on every pointer move over the plan (input.ts). Throttled with a
    trailing send, so the last position after a burst always goes out. */
let trailing: ReturnType<typeof setTimeout> | null = null;
let pendingPos: { x: number; y: number } | null = null;
export function reportCursor(x: number, y: number): void {
  if (!presenceKey) return;
  const now = Date.now(); lastMoveAt = now;
  const me = (window as any).__phUser?.uid as string | undefined;
  const alone = !others(state.presence, me || '', now).length;
  if (alone && !state.collab) return;                        // the owner alone: not a single write
  if (!shouldSend(lastSent, x, y, now)) {
    pendingPos = { x, y };
    if (!trailing && lastSent) trailing = setTimeout(() => { trailing = null; const p = pendingPos; pendingPos = null; if (p) reportCursor(p.x, p.y); }, Math.max(50, SEND_EVERY_MS - (now - lastSent.at)));
    return;
  }
  pendingPos = null;
  lastSent = { x, y, at: now };
  const [owner, name] = presenceKey.split('/');
  publishPresence(owner, name, me!, { x, y, name: editorLabel(), color: colorFor(me!) }).catch(() => {});
}
setInterval(() => { if (presenceKey && lastSent && Date.now() - lastMoveAt > IDLE_MS) lastSent = null; }, 10_000);
addEventListener('pagehide', stopPresence);

/* ---- multi-floor projects: "<project> · <floor>" layouts, a floor bar, the floor below as an underlay ---- */
export async function addFloor(): Promise<void> {
  if (!state.name || state.shareView) return;
  let p = projectOf(state.name);
  if (!p) {
    // the open layout becomes the project's ground floor
    const ground = await ask('This plan becomes a project. Name this floor', 'Ground');
    if (!ground) return;
    await renameLayout(floorName(state.name, ground));
    p = projectOf(state.name)!;
  }
  const names = (await listLayouts()).map(l => l.name);
  const label = await ask('New floor', nextFloorLabel(floorsOf(names, p.project)));
  if (!label) return;
  const name = floorName(p.project, label);
  if (names.includes(name)) { notice(`There is already a floor called “${label}” in ${p.project}.`); return; }
  if (state.dirty) await saveNow();
  // the new floor starts from this floor's outside walls (what upper floors usually share)
  const walls = exteriorWalls(state.doc.walls).map(w => ({ ...w }));
  const doc = { ...emptyDoc(), walls };
  await backend.save(name, JSON.stringify(doc, stripRuntime));
  await listLayouts();
  await loadLayout(name);
}
export function renderFloorBar(): void {
  const bar = $('#floorBar');
  bar.innerHTML = '';
  const p = projectOf(state.name);
  if (!p || state.shareView) { bar.style.display = 'none'; return; }
  const floors = floorsOf(lastList.map(l => l.name), p.project);
  if (floors.length < 2) { bar.style.display = 'none'; return; }
  bar.style.display = '';
  for (const f of floors) {
    const b = document.createElement('button');
    b.className = 'floor' + (f === state.name ? ' current' : '');
    b.textContent = projectOf(f)!.floor;
    b.title = f;
    b.setAttribute('aria-pressed', String(f === state.name));
    b.addEventListener('click', async () => { if (f !== state.name) { if (state.dirty) await saveNow(); await loadLayout(f); } });
    bar.appendChild(b);
  }
}
async function loadUnderlay(): Promise<void> {
  const p = projectOf(state.name);
  const below = p && !state.shareView ? floorBelow(floorsOf(lastList.map(l => l.name), p.project), state.name!) : null;
  if (!below) { if (state.underlay) { state.underlay = null; requestDraw(); } return; }
  if (state.underlay?.name === below) return;
  const rec = await backend.load(below).catch(() => null);
  state.underlay = rec ? { name: below, doc: normalizeDoc(Object.assign(emptyDoc(), rec.doc)) } : null;
  requestDraw();
}

/* ---- Paste JSON: layouts written by hand or by an AI assistant ---- */
const pasteModal = $('#pasteModal'), pasteText = $('#pasteText') as HTMLTextAreaElement, pasteName = $('#pasteName') as HTMLInputElement;
export function openPaste(): void {
  pasteText.value = ''; pasteName.value = ''; $('#pasteMsg').textContent = '';
  pasteModal.classList.add('show');
  setTimeout(() => pasteText.focus(), 0);
}
async function importPasted(): Promise<void> {
  const list = parsePasted(pasteText.value);
  if (!list) { $('#pasteMsg').textContent = 'That doesn’t look like a Playout layout — it needs a "walls" list. Ask your assistant for Playout JSON, or export one to see the shape.'; return; }
  const taken = new Set((await listLayouts()).map(l => l.name));
  const rep = { placed: 0, dropped: 0 };
  let last: string | null = null;
  for (const [i, p] of list.entries()) {
    const name = layoutName(i === 0 && pasteName.value.trim() ? pasteName.value : p.name, 'pasted', taken);
    taken.add(name);
    await backend.save(name, JSON.stringify(normalizeDoc(p.doc as unknown as ReturnType<typeof emptyDoc>, rep), stripRuntime));
    last = name;
  }
  pasteModal.classList.remove('show');
  if (last) await loadLayout(last);
  const msgs: string[] = [`Imported ${list.length} layout${list.length > 1 ? 's' : ''}.`];
  if (rep.placed) msgs.push(`${rep.placed} door/window${rep.placed > 1 ? 's' : ''} written as line segments were snapped onto the nearest wall.`);
  if (rep.dropped) msgs.push(`${rep.dropped} door/window${rep.dropped > 1 ? 's' : ''} couldn’t be placed on any wall and were left out.`);
  if (msgs.length > 1) notice(msgs.join('\n'));
}
$('#pasteGo').addEventListener('click', importPasted);
$('#pasteClose').addEventListener('click', () => pasteModal.classList.remove('show'));
pasteModal.addEventListener('click', e => { if (e.target === pasteModal) pasteModal.classList.remove('show'); });
pasteText.addEventListener('keydown', e => { e.stopPropagation(); if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') importPasted(); });
pasteName.addEventListener('keydown', e => e.stopPropagation());

/* ---- "your plan lives only in this browser" nudge (once, after real work) ---- */
const nudgeStore = (() => { try { return localStorage; } catch { return null; } })();
let nudgeState = loadNudge(nudgeStore);
function countDeviceSave(): void {
  const r = recordSave(nudgeState, { cloudAvailable: cloudReady(), signedIn: !!(window as any).__phUser, shareView: !!state.shareView });
  nudgeState = r.st; saveNudge(nudgeStore, nudgeState);
  if (r.nudge) showNudge();
}
export function showNudge(): void { $('#nudgeBar').classList.add('show'); }
function hideNudge(): void {
  $('#nudgeBar').classList.remove('show');
  nudgeState = { ...nudgeState, nudged: true }; saveNudge(nudgeStore, nudgeState);
}
$('#nudgeSignIn').addEventListener('click', () => { hideNudge(); openSignIn(); });
$('#nudgeLater').addEventListener('click', hideNudge);

/** Open a print-ready page with the plan; the browser's print dialog does PDF. */
export function exportPrint(): void {
  const cv = renderExportCanvas();
  if (!cv) { notice('Nothing to print yet — draw a room first.'); return; }
  const w = window.open('', '_blank');
  if (!w) { notice('Your browser blocked the print window. Allow pop-ups for this site and try again.'); return; }
  w.document.write(printHtml(state.name || 'Floor plan', cv.toDataURL('image/png'), cv.width / 2, cv.height / 2));
  w.document.close();
}

/* ---- layout menu ---- */
const layoutMenu = $('#layoutMenu');
$('#layoutBtn').addEventListener('click', async e => {
  e.stopPropagation();
  if (layoutMenu.classList.contains('show')) { layoutMenu.classList.remove('show'); return; }
  const list = await listLayouts();
  layoutMenu.innerHTML = '';
  for (const l of list) {
    const b = document.createElement('button');
    b.className = l.name === state.name ? 'current' : '';
    const when = new Date(l.mtime * 1000);
    b.innerHTML = `<span></span><span class="when"></span>`;
    (b.firstChild as HTMLElement).textContent = l.name;
    (b.lastChild as HTMLElement).textContent = when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    b.addEventListener('click', async () => {
      layoutMenu.classList.remove('show');
      if (l.name !== state.name) { if (state.dirty) await saveNow(); await loadLayout(l.name); }
    });
    layoutMenu.appendChild(b);
  }
  if (!state.collab && sharedWithMe && sharedWithMe.layouts.length) {
    const s2 = document.createElement('div'); s2.className = 'sep'; layoutMenu.appendChild(s2);
    for (const nm of sharedWithMe.layouts) {
      const b = document.createElement('button'); b.className = 'shared';
      b.textContent = `✎ ${nm} — shared with you`;
      const o = sharedWithMe.owner;
      b.addEventListener('click', async () => { layoutMenu.classList.remove('show'); await enterCollab(o, sharedWithMe!.layouts, nm); });
      layoutMenu.appendChild(b);
    }
  }
  const sep = document.createElement('div'); sep.className = 'sep'; layoutMenu.appendChild(sep);
  const entries: [string, () => void | Promise<void>][] = state.collab ? [
    ['Version history…', openHistory],
    ['Export JSON', exportCurrent],
    ['Export PNG', exportPNG],
    ['Print / save as PDF…', exportPrint],
    ['Back to my plans', () => exitCollab()],
  ] : state.shareView ? [
    ['Save a copy…', saveShareCopy],
    ['New layout…', async () => { const n = await ask('New layout', 'e.g. first-floor'); if (n) await createLayout(n); }],
  ] : [
    ['New layout…', async () => { const n = await ask('New layout', 'e.g. first-floor'); if (n) { if (state.dirty) await saveNow(); await createLayout(n); } }],
    ['Share layout…', shareLayout],
    ['Rename…', async () => {
      const n = await ask('Rename layout', state.name || '');
      if (n && n !== state.name) await renameLayout(n);
    }],
    ['Add a floor…', addFloor],
    ['Duplicate…', async () => {
      const n = await ask('Duplicate as', state.name + '-copy');
      if (n) {
        state.name = n; await saveNow();
        $('#layoutBtn').textContent = n;
        $('#vrBtn').setAttribute('href', '/vr?name=' + encodeURIComponent(n));
        history.replaceState(null, '', '?layout=' + encodeURIComponent(n));
      }
    }],
    ['Version history…', openHistory],
    ['Import JSON…', importFiles],
    ['Paste JSON…', openPaste],
    ['Export JSON', exportCurrent],
    ['Export PNG', exportPNG],
    ['Print / save as PDF…', exportPrint],
    ['Delete layout', async () => {
      if (!await confirmBox(`Delete “${state.name}”? This can't be undone.`, 'Delete', true)) return;
      const user = (window as any).__phUser;
      if (backend.kind === 'cloud' && user) await revokeShare(user.uid, state.name!).catch(() => {});
      await backend.remove(state.name!);
      const list2 = await listLayouts();
      if (list2.length) await loadLayout(list2[0].name);
      else await createLayout('home');
    }],
  ];
  for (const [txt, fn] of entries) {
    const b = document.createElement('button'); b.textContent = txt;
    b.addEventListener('click', () => { layoutMenu.classList.remove('show'); fn(); });
    layoutMenu.appendChild(b);
  }
  layoutMenu.classList.add('show');
});
document.addEventListener('click', e => {
  if (!layoutMenu.contains(e.target as Node) && (e.target as HTMLElement).id !== 'layoutBtn') layoutMenu.classList.remove('show');
});

function ask(title: string, placeholder: string): Promise<string | null> {
  return new Promise(resolve => {
    const modal = $('#modal');
    $('#modalTitle').textContent = title;
    const inp = $('#modalInput') as HTMLInputElement;
    inp.value = ''; inp.placeholder = placeholder || '';
    modal.classList.add('show'); inp.focus();
    const done = (v: string | null) => { modal.classList.remove('show'); cleanup(); resolve(v); };
    const ok = () => { const v = inp.value.trim(); done(/^[\w][\w \-.]{0,60}$/.test(v) ? v : null); };
    const cancel = () => done(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Enter') ok(); if (e.key === 'Escape') cancel(); e.stopPropagation(); };
    function cleanup() {
      $('#modalOk').removeEventListener('click', ok);
      $('#modalCancel').removeEventListener('click', cancel);
      inp.removeEventListener('keydown', onKey);
    }
    $('#modalOk').addEventListener('click', ok);
    $('#modalCancel').addEventListener('click', cancel);
    inp.addEventListener('keydown', onKey);
  });
}
